import { trace } from "@opentelemetry/api";
import hashjs from "hash.js";
import ms, { type StringValue } from "ms";
import { z } from "zod/v3";

import {
  defaultMaxRetries,
  ExecutionVersion,
  headerKeys,
  internalEvents,
} from "../../helpers/consts.ts";

import {
  deserializeError,
  ErrCode,
  serializeError,
} from "../../helpers/errors.js";
import { undefinedToNull } from "../../helpers/functions.js";
import { isDeferredFunction } from "../../helpers/marker.ts";
import {
  createDeferredPromise,
  createDeferredPromiseWithStack,
  createTimeoutPromise,
  type DeferredPromiseReturn,
  type GoInterval,
  goIntervalTiming,
  resolveAfterPending,
  resolveNextTick,
  retryWithBackoff,
  runAsPromise,
} from "../../helpers/promises.ts";
import * as Temporal from "../../helpers/temporal.ts";
import {
  isRecord,
  type MaybePromise,
  type Simplify,
} from "../../helpers/types.ts";
import {
  type APIStepPayload,
  type Context,
  type DeferFn,
  type EventPayload,
  type FailureEventArgs,
  type Handler,
  type HashedOp,
  jsonErrorSchema,
  type OutgoingOp,
  StepMode,
  StepOpCode,
} from "../../types.ts";
import { version } from "../../version.ts";
import { internalLoggerSymbol } from "../Inngest.ts";
import { createGroupTools } from "../InngestGroupTools.ts";
import type {
  MetadataKind,
  MetadataOpcode,
  MetadataScope,
  MetadataUpdate,
} from "../InngestMetadata.ts";
import {
  createStepTools,
  type ExperimentStepTools,
  experimentStepRunSymbol,
  type FoundStep,
  getStepOptions,
  STEP_INDEXING_SUFFIX,
  type StepHandler,
} from "../InngestStepTools.ts";
import { MiddlewareManager } from "../middleware/index.ts";
import type { Middleware } from "../middleware/middleware.ts";
import { UnreachableError } from "../middleware/utils.ts";
import { NonRetriableError } from "../NonRetriableError.ts";
import { RetryAfterError } from "../RetryAfterError.ts";
import { StepError } from "../StepError.ts";
import { Stream } from "../StreamTools.ts";
import { validateEvents } from "../triggers/utils.js";
import { getAsyncCtx, getAsyncLocalStorage } from "./als.ts";
import {
  type BasicFoundStep,
  type ExecutionResult,
  type IInngestExecution,
  InngestExecution,
  type InngestExecutionFactory,
  type InngestExecutionOptions,
  type MemoizedOp,
} from "./InngestExecution.ts";
import { isLazyOp, LazyOps } from "./lazyOps.ts";
import { clientProcessorMap } from "./otel/access.ts";
import {
  buildSseMetadataEvent,
  prependToStream,
  type SseResponse,
} from "./streaming.ts";

const { sha1 } = hashjs;

/**
 * Retry configuration for checkpoint operations.
 *
 * Checkpoint calls use exponential backoff with jitter to handle transient
 * network failures (e.g., dev server temporarily down, cloud hiccup). If
 * retries exhaust, the error propagates up - for Sync mode this results in a
 * 500 error, for AsyncCheckpointing the caller handles fallback.
 */
const CHECKPOINT_RETRY_OPTIONS = { maxAttempts: 5, baseDelay: 100 };

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (isRecord(error) && typeof error.message === "string") {
    return error.message;
  }
  return String(error);
}

/**
 * Placeholder step ID used when completing a checkpointed run.
 */
const RUN_COMPLETE_STEP_ID = "complete";

const STEP_NOT_FOUND_MAX_FOUND_STEPS = 25;

export const createExecutionEngine: InngestExecutionFactory = (options) => {
  return new InngestExecutionEngine(options);
};

function extractSseResponse(response: Response, body: string): SseResponse {
  const headers: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    headers[key] = value;
  });
  return { body, statusCode: response.status, headers };
}

function defaultSseResponse(data: unknown): SseResponse {
  return {
    body: JSON.stringify(data),
    statusCode: 200,
    headers: { "content-type": "application/json" },
  };
}

function jsonResponse(data: unknown, status = 200): Response {
  return new Response(JSON.stringify(data), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

class InngestExecutionEngine
  extends InngestExecution
  implements IInngestExecution
{
  public version = ExecutionVersion.V2;

  private state: ExecutionState;
  private fnArg: Context.Any;
  private checkpointHandlers: CheckpointHandlers;
  private timeoutDuration = 1000 * 10;
  private execution: Promise<ExecutionResult> | undefined;
  private userFnToRun: Handler.Any;
  private middlewareManager: MiddlewareManager;
  /**
   * Close the stream via {@link streamCloseSucceeded}, {@link streamCloseFailed},
   * or {@link streamEnd} — never call `streamTools.close*`/`end` directly, as
   * the wrappers ensure the redirect event is flushed first.
   */
  private streamTools: Stream;

  /**
   * Resolved when `stream.push()`/`pipe()` is first called in sync mode,
   * allowing `_start()` to return the SSE Response to the HTTP layer while
   * the core loop continues executing steps in the background.
   */
  private earlyStreamResponse:
    | DeferredPromiseReturn<ExecutionResult>
    | undefined;

  /**
   * Whether the `inngest.redirect_info` SSE event has already been sent.
   * Prevents duplicate redirect events.
   */
  private redirectSent = false;

  /**
   * Promise that resolves once the redirect event has been written (or the
   * attempt completes). Stored so that `checkpointAndSwitchToAsync` can
   * await it before closing the writer.
   */
  private redirectPromise: Promise<void> = Promise.resolve();

  /**
   * If we're supposed to run a particular step via `requestedRunStep`, this
   * will be a `Promise` that resolves after no steps have been found for
   * `timeoutDuration` milliseconds.
   *
   * If we're not supposed to run a particular step, this will be `undefined`.
   */
  private timeout?: ReturnType<typeof createTimeoutPromise>;
  private rootSpanId?: string;

  /**
   * If we're checkpointing and have been given a maximum runtime, this will be
   * a `Promise` that resolves after that duration has elapsed, allowing us to
   * ensure that we end the execution in good time, especially in serverless
   * environments.
   */
  private checkpointingMaxRuntimeTimer?: ReturnType<
    typeof createTimeoutPromise
  >;

  /**
   * If we're checkpointing and have been given a maximum buffer interval, this
   * will be a `Promise` that resolves after that duration has elapsed, allowing
   * us to periodically checkpoint even if the step buffer hasn't filled.
   */
  private checkpointingMaxBufferIntervalTimer?: ReturnType<
    typeof createTimeoutPromise
  >;

  constructor(rawOptions: InngestExecutionOptions) {
    const options: InngestExecutionOptions = {
      ...rawOptions,
      stepMode: rawOptions.stepMode ?? StepMode.Async,
    };

    super(options);

    /**
     * Check we have everything we need for checkpointing
     */
    if (this.options.stepMode === StepMode.Sync) {
      if (!this.options.createResponse) {
        throw new Error("createResponse is required for sync step mode");
      }
    }

    this.userFnToRun = this.getUserFnToRun();
    this.streamTools = new Stream({
      onActivated: () => this.handleStreamActivated(),
      onWriteError: (err) =>
        this.devDebug(
          "stream write error (client may have disconnected):",
          err,
        ),
    });
    this.state = this.createExecutionState();
    this.fnArg = this.createFnArg();

    // Setup middleware
    const mwInstances =
      this.options.middlewareInstances ??
      (this.options.client.middleware || []).map((Cls) => {
        return new Cls({ client: this.options.client });
      });
    this.middlewareManager = new MiddlewareManager(
      this.fnArg,
      () => this.state.stepState,
      mwInstances,
      this.options.fn,
      this.options.client[internalLoggerSymbol],
    );

    this.checkpointHandlers = this.createCheckpointHandlers();
    this.initializeTimer(this.state);
    this.initializeCheckpointRuntimeTimer(this.state);

    this.devDebug(
      "created new V1 execution for run;",
      this.options.requestedRunStep
        ? `wanting to run step "${this.options.requestedRunStep}"`
        : "discovering steps",
    );

    this.devDebug("existing state keys:", Object.keys(this.state.stepState));
  }

  /**
   * Idempotently start the execution of the user's function.
   */
  public start() {
    if (!this.execution) {
      this.devDebug("starting V1 execution");

      const tracer = trace.getTracer("inngest", version);

      this.execution = getAsyncLocalStorage().then((als) => {
        return als.run(
          {
            app: this.options.client,
            execution: {
              ctx: this.fnArg,
              instance: this,
              stream: this.streamTools,
            },
          },
          async () => {
            return tracer.startActiveSpan("inngest.execution", (span) => {
              this.rootSpanId = span.spanContext().spanId;
              clientProcessorMap.get(this.options.client)?.declareStartingSpan({
                span,
                runId: this.options.runId,
                traceparent: this.options.headers[headerKeys.TraceParent],
                tracestate: this.options.headers[headerKeys.TraceState],
              });

              return this._start()
                .then((result) => {
                  this.devDebug("result:", result);
                  return result;
                })
                .finally(() => {
                  span.end();
                });
            });
          },
        );
      });
    }

    return this.execution;
  }

  public addMetadata(
    stepId: string,
    kind: MetadataKind,
    scope: MetadataScope,
    op: MetadataOpcode,
    values: Record<string, unknown>,
  ) {
    if (!this.state.metadata) {
      this.state.metadata = new Map();
    }

    const updates = this.state.metadata.get(stepId) ?? [];
    updates.push({ kind, scope, op, values });
    this.state.metadata.set(stepId, updates);

    return true;
  }

  /**
   * Starts execution of the user's function and the core loop.
   */
  private async _start(): Promise<ExecutionResult> {
    // Set up a deferred promise that handleStreamActivated resolves to return
    // the SSE Response early while the core loop keeps running.
    if (this.options.stepMode === StepMode.Sync && this.options.acceptsSse) {
      this.earlyStreamResponse = createDeferredPromise<ExecutionResult>();
    }

    const coreLoop = this.runCoreLoop();

    if (this.earlyStreamResponse) {
      // Suppress: if earlyStreamResponse wins the race and coreLoop later
      // rejects, the rejection must not go unhandled.
      coreLoop.catch((err) => {
        this.options.client[internalLoggerSymbol].error(
          { err },
          "Core loop rejected after early stream response was sent",
        );
      });

      return Promise.race([this.earlyStreamResponse.promise, coreLoop]);
    }

    return coreLoop;
  }

  /**
   * The core checkpoint loop: processes checkpoints until a handler returns
   * a result.
   */
  private async runCoreLoop(): Promise<ExecutionResult> {
    try {
      const allCheckpointHandler = this.getCheckpointHandler("");
      await this.startExecution();

      let i = 0;

      for await (const checkpoint of this.state.loop) {
        await allCheckpointHandler(checkpoint, i);

        const handler = this.getCheckpointHandler(checkpoint.type);
        const result = await handler(checkpoint, i++);

        if (result) {
          return result;
        }
      }
    } catch (error) {
      // If earlyStreamResponse was set up, close the stream with an error event
      // and resolve (not reject) with a well-formed result so the caller gets a
      // structured response instead of a raw Error.
      if (this.earlyStreamResponse) {
        await this.streamCloseFailed("Internal execution error");
        const result = this.transformOutput({ error });
        this.earlyStreamResponse.resolve(result);
        return result;
      }

      return this.transformOutput({ error });
    } finally {
      void this.state.loop.return();
    }

    /**
     * If we're here, the generator somehow finished without returning a value.
     * This should never happen.
     */
    throw new Error("Core loop finished without returning a value");
  }

  private async checkpoint(steps: OutgoingOp[]): Promise<void> {
    const lazyOps = this.state.lazyOps.drain();
    if (lazyOps.length > 0) {
      steps = [...steps, ...lazyOps];
    }

    if (this.options.stepMode === StepMode.Sync) {
      if (!this.state.checkpointedRun) {
        // We have to start the run
        const res = await retryWithBackoff(
          () =>
            this.options.client["inngestApi"].checkpointNewRun({
              runId: this.fnArg.runId,
              event: this.fnArg.event as APIStepPayload,
              steps,
              executionVersion: this.version,
              retries: this.fnArg.maxAttempts ?? defaultMaxRetries,
            }),
          CHECKPOINT_RETRY_OPTIONS,
        );

        this.state.checkpointedRun = {
          appId: res.data.app_id,
          fnId: res.data.fn_id,
          token: res.data.token,
          realtimeToken: res.data.realtime_token,
        };

        // Try sending redirect (no-op if stream isn't activated yet).
        this.sendRedirectIfReady();
      } else {
        await retryWithBackoff(
          () =>
            this.options.client["inngestApi"].checkpointSteps({
              appId: this.state.checkpointedRun!.appId,
              fnId: this.state.checkpointedRun!.fnId,
              runId: this.fnArg.runId,
              steps,
            }),
          CHECKPOINT_RETRY_OPTIONS,
        );
      }
    } else if (this.options.stepMode === StepMode.AsyncCheckpointing) {
      const { internalFnId, queueItemId } = this.options;
      if (!queueItemId) {
        throw new Error(
          "Missing queueItemId for async checkpointing. This is a bug in the Inngest SDK.",
        );
      }

      if (!internalFnId) {
        throw new Error(
          "Missing internalFnId for async checkpointing. This is a bug in the Inngest SDK.",
        );
      }

      await retryWithBackoff(
        () =>
          this.options.client["inngestApi"].checkpointStepsAsync({
            runId: this.fnArg.runId,
            fnId: internalFnId,
            queueItemId,
            steps,
          }),
        CHECKPOINT_RETRY_OPTIONS,
      );
    } else {
      throw new Error(
        "Checkpointing is only supported in Sync and AsyncCheckpointing step modes. This is a bug in the Inngest SDK.",
      );
    }
  }

  private async checkpointAndSwitchToAsync(
    steps: OutgoingOp[],
    stepError?: unknown,
  ): Promise<ExecutionResult> {
    await this.checkpoint(steps);

    if (!this.state.checkpointedRun?.token) {
      throw new Error("Failed to checkpoint and switch to async mode");
    }

    const token = this.state.checkpointedRun.token;

    if (this.streamTools.activated) {
      if (stepError && !this.retriability(stepError)) {
        // Permanent failure — close with a failed event so the client
        // sees the error.
        await this.streamCloseFailed(errorMessage(stepError));
      } else {
        // End stream without a result event — client uses redirect_info
        // to reconnect.
        await this.streamEnd();
      }
    } else if (this.options.acceptsSse) {
      // Stream was never activated (no stream.push/pipe), but the client
      // accepts SSE. Close and return the SSE response so the client gets
      // metadata + redirect_info events and can follow the redirect.
      await this.streamEnd();
      return {
        type: "function-resolved",
        ctx: this.fnArg,
        ops: this.ops,
        data: this.buildSyncSseResponse(),
      };
    }

    return {
      type: "change-mode",
      ctx: this.fnArg,
      ops: this.ops,
      to: StepMode.Async,
      token,
    };
  }

  /**
   * Prepend the `inngest.metadata` SSE event to the stream's readable side.
   * The returned stream can be used as a fetch body or Response body.
   *
   * NOTE: `this.streamTools.readable` can only be consumed once, so only one
   * of `buildSyncSseResponse` or `postCheckpointStream` may be called per
   * execution.
   */
  private buildMetadataPrefixedStream(): ReadableStream<Uint8Array> {
    const metadataEvent = buildSseMetadataEvent(this.fnArg.runId);
    return prependToStream(
      new TextEncoder().encode(metadataEvent),
      this.streamTools.readable,
    );
  }

  /**
   * Build the initial SSE `Response` that marks the start of streaming to the
   * client. Only used in sync mode. In async mode, the stream is POSTed to the
   * Inngest Server via {@link postCheckpointStream} instead.
   *
   * The response body is the stream's readable side, prefixed with the
   * `inngest.metadata` SSE event.
   */
  private buildSyncSseResponse(): Response {
    return new Response(this.buildMetadataPrefixedStream(), {
      status: 200,
      headers: {
        "Content-Type": "text/event-stream",
        "Cache-Control": "no-cache",
      },
    });
  }

  /**
   * Wraps a plain return value as an SSE Response.
   *
   * Used when the client sent `Accept: text/event-stream` but
   * `stream.push()`/`pipe()` was NOT called during execution. The
   * checkpointable data is the function's return value. The SSE events are just
   * a delivery mechanism.
   */
  private async wrapResultAsSse(
    checkpoint: Simplify<
      { type: "function-resolved" } & Checkpoints["function-resolved"]
    >,
    sseResponse: SseResponse,
  ): Promise<ExecutionResult> {
    const resultData: unknown = checkpoint.data;

    // Close the stream with a terminal succeeded event
    await this.streamCloseSucceeded(sseResponse);

    const clientResponse = this.buildSyncSseResponse();

    // Run transformOutput to fire middleware hooks
    const result = this.transformOutput({ data: resultData });

    const streamingResult: ExecutionResult = {
      ...result,
      data: clientResponse,
    } as ExecutionResult;

    // Background: checkpoint the return value (not the stream bytes).
    void this.checkpointReturnValue(resultData);

    return streamingResult;
  }

  /**
   * Called when `stream.push()`/`pipe()` is first invoked during sync
   * execution. Resolves {@link earlyStreamResponse} so that `_start()` can
   * return the SSE Response to the HTTP layer immediately, while the core
   * checkpoint loop keeps running steps in the background.
   */
  private handleStreamActivated(): undefined {
    if (this.earlyStreamResponse) {
      // "function-resolved" tells the HTTP layer to send the Response,
      // even though the function is still running.
      this.earlyStreamResponse.resolve({
        type: "function-resolved",
        ctx: this.fnArg,
        ops: this.ops,
        data: this.buildSyncSseResponse(),
      });

      // Checkpoint may have already provided the realtime token — try redirect now.
      this.sendRedirectIfReady();

      return undefined;
    }

    // Async/AsyncCheckpointing mode: start the streaming POST immediately.
    // In sync mode without SSE, the stream has no consumer — skip the POST.
    if (this.options.stepMode !== StepMode.Sync) {
      this.postCheckpointStream();
    }
    this.sendRedirectIfReady();
    return undefined;
  }

  /**
   * Sends the `inngest.redirect_info` SSE event when both conditions are met:
   * 1. The client accepts SSE (so there's a stream to write the event to)
   * 2. We have a realtime token (first checkpoint has completed)
   *
   * Called after the first checkpoint AND on stream activation, whichever
   * comes second, so the redirect is sent as early as possible.
   */
  private sendRedirectIfReady(): void {
    if (this.redirectSent) {
      return;
    }

    if (!this.options.acceptsSse) {
      return;
    }

    if (!this.state.checkpointedRun) {
      // This is part of the happy path. We may not have checkpointed the run
      // yet, which happens after the first step ends
      return;
    }

    this.redirectSent = true;

    const { realtimeToken } = this.state.checkpointedRun;

    this.redirectPromise = (async () => {
      try {
        const redirect =
          await this.options.client["inngestApi"].getRealtimeStreamRedirect(
            realtimeToken,
          );

        this.streamTools.sendRedirectInfo({
          runId: this.fnArg.runId,
          url: redirect.url,
        });
      } catch (err) {
        this.options.client[internalLoggerSymbol].warn(
          { err },
          "Failed to fetch realtime stream redirect URL",
        );
      }
    })();
  }

  /**
   * Await the pending redirect-info fetch, then close the stream with a
   * succeeded result. Awaiting first guarantees the redirect event is
   * enqueued on the write chain before the close event.
   */
  private async streamCloseSucceeded(response: SseResponse): Promise<void> {
    await this.redirectPromise;
    this.streamTools.closeSucceeded(response);
  }

  /**
   * Await the pending redirect-info fetch, then close the stream with a
   * failed result.
   */
  private async streamCloseFailed(error: string): Promise<void> {
    await this.redirectPromise;
    this.streamTools.closeFailed(error);
  }

  /**
   * Await the pending redirect-info fetch, then close the stream without
   * a result event.
   */
  private async streamEnd(): Promise<void> {
    await this.redirectPromise;
    this.streamTools.end();
  }

  /**
   * POST stream data to the checkpoint stream ingest endpoint.
   *
   * Called eagerly from handleStreamActivated so chunks flow in
   * real-time, or after completion if stream.push() was never called.
   */
  private postCheckpointStream(): void {
    try {
      // Fire and forget — completes when the stream closes.
      void this.options.client["inngestApi"]
        .checkpointStream({
          runId: this.fnArg.runId,
          body: this.buildMetadataPrefixedStream(),
        })
        .catch((err: unknown) => {
          this.devDebug("checkpoint stream POST error:", err);
        });
    } catch (err) {
      this.devDebug("checkpoint stream POST error:", err);
    }
  }

  /**
   * Checkpoints the return value of a function that was delivered via SSE.
   * Runs in the background so it doesn't block the client stream.
   */
  private async checkpointReturnValue(data: unknown): Promise<void> {
    try {
      if (this.options.createResponse) {
        await this.checkpoint([
          {
            op: StepOpCode.RunComplete,
            id: hashId(RUN_COMPLETE_STEP_ID),
            data: await this.options.createResponse(jsonResponse(data)),
          },
        ]);
      }
    } catch (err) {
      this.devDebug(
        "error during background checkpoint of SSE result, client stream unaffected:",
        err,
      );
    }
  }

  /**
   * Creates a handler for every checkpoint type, defining what to do when we
   * reach that checkpoint in the core loop.
   */
  private createCheckpointHandlers(): CheckpointHandlers {
    const commonCheckpointHandler: CheckpointHandlers[StepMode][""] = (
      checkpoint,
    ) => {
      this.devDebug(`${this.options.stepMode} checkpoint:`, checkpoint);
    };

    const stepRanHandler = async (
      stepResult: OutgoingOp,
    ): Promise<ExecutionResult> => {
      const transformResult = await this.transformOutput(stepResult);

      /**
       * Transforming output will always return either function rejection or
       * resolution. In most cases, this can be immediately returned, but in
       * this particular case we want to handle it differently.
       */
      if (transformResult.type === "function-resolved") {
        return {
          type: "step-ran",
          ctx: transformResult.ctx,
          ops: transformResult.ops,
          step: {
            ...stepResult,
            data: transformResult.data,
          },
        };
      } else if (transformResult.type === "function-rejected") {
        const stepForResponse = {
          ...stepResult,
          error: transformResult.error,
        };

        if (stepResult.op === StepOpCode.StepFailed) {
          const ser = serializeError(transformResult.error);
          stepForResponse.data = {
            __serialized: true,
            name: ser.name,
            message: ser.message,
            stack: "",
          };
        }

        return {
          type: "step-ran",
          ctx: transformResult.ctx,
          ops: transformResult.ops,
          retriable: transformResult.retriable,
          step: stepForResponse,
        };
      }

      return transformResult;
    };

    const maybeReturnNewSteps = async (): Promise<
      ExecutionResult | undefined
    > => {
      const newSteps = await this.filterNewSteps(
        Array.from(this.state.steps.values()),
      );

      const allSteps: OutgoingOp[] = [
        ...(newSteps ?? []),
        ...this.state.lazyOps.drain(),
      ];
      if (allSteps.length === 0) {
        return;
      }

      return {
        type: "steps-found",
        ctx: this.fnArg,
        ops: this.ops,
        steps: allSteps as [OutgoingOp, ...OutgoingOp[]],
      };
    };

    const attemptCheckpointAndResume = async (
      stepResult?: OutgoingOp,
      resume = true,
      force = false,
    ): Promise<ExecutionResult | undefined> => {
      // If we're here, we successfully ran a step, so we may now need
      // to checkpoint it depending on the step buffer configured.
      if (stepResult) {
        const stepToResume = this.resumeStepWithResult(stepResult, resume);

        // Clear `executingStep` immediately after resuming, before any await.
        // `resumeStepWithResult` resolves the step's promise, queuing a
        // microtask for the function to continue. Any subsequent await (e.g.
        // the `transformOutput` hook) yields and lets that microtask run, so
        // `executingStep` must already be cleared to avoid a false positive
        // NESTING_STEPS warning in the next step's handler.
        delete this.state.executingStep;

        // Buffer a copy with transformed data for checkpointing
        this.state.checkpointingStepBuffer.push({
          ...stepToResume,
          data: stepResult.data,
        });
      }

      if (
        force ||
        !this.options.checkpointingConfig?.bufferedSteps ||
        this.state.checkpointingStepBuffer.length >=
          this.options.checkpointingConfig.bufferedSteps
      ) {
        this.devDebug("checkpointing and resuming execution after step run");

        try {
          this.devDebug(
            `checkpointing all buffered steps:`,
            this.state.checkpointingStepBuffer
              .map((op) => op.displayName || op.id)
              .join(", "),
          );

          return void (await this.checkpoint(
            this.state.checkpointingStepBuffer,
          ));
        } catch (err) {
          // If checkpointing fails for any reason, fall back to returning
          // ALL buffered steps to the executor via the normal async flow.
          // The executor persists completed steps and rediscovers any
          // parallel/errored steps on the next invocation.
          this.devDebug(
            "error checkpointing after step run, so falling back to async",
            err,
          );

          const buffered = this.state.checkpointingStepBuffer;

          if (buffered.length) {
            return {
              type: "steps-found" as const,
              ctx: this.fnArg,
              ops: this.ops,
              steps: buffered as [OutgoingOp, ...OutgoingOp[]],
            };
          }

          return;
        } finally {
          // Clear the checkpointing buffer
          this.state.checkpointingStepBuffer = [];
        }
      } else {
        this.devDebug(
          `not checkpointing yet, continuing execution as we haven't reached buffered step limit of ${this.options.checkpointingConfig?.bufferedSteps}`,
        );
      }

      return;
    };

    const syncHandlers: CheckpointHandlers[StepMode.Sync] = {
      /**
       * Run for all checkpoints. Best used for logging or common actions.
       * Use other handlers to return values and interrupt the core loop.
       */
      "": commonCheckpointHandler,

      "function-resolved": async (checkpoint) => {
        const usingSseStream = !!this.earlyStreamResponse;

        if (this.streamTools.activated) {
          let resultData: unknown = checkpoint.data;
          let sseResponse: SseResponse;
          if (checkpoint.data instanceof Response) {
            // Clone when not SSE so the Response body stays intact for passthrough.
            const body = await (usingSseStream
              ? checkpoint.data.text()
              : checkpoint.data.clone().text());
            sseResponse = extractSseResponse(checkpoint.data, body);
            resultData = body;
          } else {
            sseResponse = defaultSseResponse(resultData);
          }

          // Always close the stream — either the SSE client or the
          // server-side checkpoint POST needs the terminal result event.
          await this.streamCloseSucceeded(sseResponse);

          if (usingSseStream) {
            // SSE path: response already sent; checkpoint in background.
            void this.checkpointReturnValue(resultData);
            return this.transformOutput({ data: resultData });
          }

          // Non-SSE: fall through to normal response handling.
        }

        // If the client accepts SSE (but stream.push() was NOT called), build
        // the SSE Response now. The SSE envelope is always needed when the
        // client requests it because it carries inngest.metadata and
        // inngest.redirect_info. Without these the client can't reconnect if a
        // future execution goes async.
        if (this.options.acceptsSse) {
          let sseResponse: SseResponse;
          if (checkpoint.data instanceof Response) {
            const body = await checkpoint.data.text();
            sseResponse = extractSseResponse(checkpoint.data, body);
            checkpoint = { ...checkpoint, data: body };
          } else {
            sseResponse = defaultSseResponse(checkpoint.data);
          }
          return this.wrapResultAsSse(checkpoint, sseResponse);
        }

        // Response pass-through: deliver as-is. Checkpoint null because the
        // Response body (ReadableStream) can only be consumed once.
        if (checkpoint.data instanceof Response) {
          void this.checkpointReturnValue(null);
          return this.transformOutput({ data: checkpoint.data });
        }

        // Non-streaming path
        await this.checkpoint([
          {
            op: StepOpCode.RunComplete,
            id: hashId(RUN_COMPLETE_STEP_ID),
            data: await this.options.createResponse!(
              jsonResponse(checkpoint.data),
            ),
          },
        ]);

        // Apply middleware transformation before returning
        return this.transformOutput({ data: checkpoint.data });
      },

      "function-rejected": async (checkpoint) => {
        const usingSseStream = !!this.earlyStreamResponse;
        const isFinal = !this.retriability(checkpoint.error);

        if (this.streamTools.activated && usingSseStream) {
          // SSE path: checkpoint the error, then close the stream.
          // The checkpoint triggers sendRedirectIfReady which starts
          // the (near-instant) redirect URL resolution. Chaining the
          // close as a continuation ensures the redirect event is
          // written first, without blocking the handler's return on
          // the checkpoint's retry budget.
          void (async () => {
            try {
              await this.checkpoint([
                {
                  id: hashId(RUN_COMPLETE_STEP_ID),
                  op: isFinal ? StepOpCode.StepFailed : StepOpCode.StepError,
                  error: checkpoint.error,
                },
              ]);
            } catch (err) {
              this.options.client[internalLoggerSymbol].warn(
                { err },
                "Failed to checkpoint function error",
              );
            }

            if (isFinal) {
              await this.streamCloseFailed(errorMessage(checkpoint.error));
            } else {
              await this.streamEnd();
            }
          })();

          return this.transformOutput({ error: checkpoint.error });
        }

        // If the function throws during sync execution, we want to switch to
        // async mode so that we can retry. The exception is that we're already
        // at max attempts, in which case we do actually want to reject.
        if (isFinal) {
          return this.transformOutput({ error: checkpoint.error });
        }

        // Retryable — checkpoint the error and switch to async mode.
        // checkpointAndSwitchToAsync handles closing the stream.
        return this.checkpointAndSwitchToAsync([
          {
            id: hashId(RUN_COMPLETE_STEP_ID),
            op: StepOpCode.StepError,
            error: checkpoint.error,
          },
        ]);
      },

      "step-not-found": () => {
        return {
          type: "function-rejected",
          ctx: this.fnArg,
          error: new Error(
            "Step not found when checkpointing; this should never happen",
          ),
          ops: this.ops,
          retriable: false,
        };
      },

      "steps-found": async ({ steps }) => {
        // If we're entering parallelism or async mode, checkpoint and switch
        // to async.
        if (steps.length !== 1 || steps[0].mode !== StepMode.Sync) {
          return this.checkpointAndSwitchToAsync(
            steps.map((step) => ({ ...step, id: step.hashedId })),
          );
        }

        // Otherwise we're good to start executing things right now.
        const result = await this.executeStep(steps[0]);

        const transformed = await stepRanHandler(result);
        if (transformed.type !== "step-ran") {
          throw new Error(
            "Unexpected checkpoint handler result type after running step in sync mode",
          );
        }

        if (result.error) {
          return this.checkpointAndSwitchToAsync(
            [transformed.step],
            result.error,
          );
        }

        // Resume the step with original data for user code
        //
        // Note: We should likely also pass this through `transformOutput` and
        // then `transformInput` to mimic the entire middleware cycle, to ensure
        // that any transformations that purposefully skew the resulting type
        // are supported.
        const stepToResume = this.resumeStepWithResult(result);

        // Clear executingStep now that the step result has been processed.
        // Without this, the next step discovery would see stale state and
        // emit a false positive NESTING_STEPS warning.
        delete this.state.executingStep;

        // Checkpoint with transformed data from middleware
        const stepForCheckpoint = {
          ...stepToResume,
          data: transformed.step.data,
        };

        return void (await this.checkpoint([stepForCheckpoint]));
      },

      "checkpointing-runtime-reached": () => {
        return this.checkpointAndSwitchToAsync([
          {
            op: StepOpCode.DiscoveryRequest,

            // Append with time because we don't want Executor-side
            // idempotency to dedupe. There may have been a previous
            // discovery request.
            id: _internals.hashId(`discovery-request-${Date.now()}`),
          },
        ]);
      },

      "checkpointing-buffer-interval-reached": () => {
        return attemptCheckpointAndResume(undefined, false, true);
      },
    };

    const asyncHandlers: CheckpointHandlers[StepMode.Async] = {
      /**
       * Run for all checkpoints. Best used for logging or common actions.
       * Use other handlers to return values and interrupt the core loop.
       */
      "": commonCheckpointHandler,

      /**
       * The user's function has completed and returned a value.
       */
      "function-resolved": async ({ data }) => {
        let resultData: unknown = data;
        let sseResponse: SseResponse;
        if (data instanceof Response) {
          const body = await data.text();
          sseResponse = extractSseResponse(data, body);
          resultData = body;
        } else {
          sseResponse = defaultSseResponse(resultData);
        }

        // Real new steps (e.g. from `Promise.race` where the winning branch
        // completed before losing branches reported). The function isn't truly
        // done: the executor needs to memoize these and call back. Don't close
        // the stream. `filterNewSteps` excludes lazy ops, so `attachLazyOps`
        // is what folds them in here.
        const newSteps = await this.filterNewSteps(
          Array.from(this.state.steps.values()),
        );

        if (newSteps?.length) {
          return this.attachLazyOps({
            type: "steps-found",
            ctx: this.fnArg,
            ops: this.ops,
            steps: newSteps as [OutgoingOp, ...OutgoingOp[]],
          });
        }

        // Function is truly done. Close the stream with a terminal
        // succeeded event.
        await this.streamCloseSucceeded(sseResponse);

        // If stream was never activated, start the POST now so the
        // client waiting at the GET endpoint gets the result event.
        if (!this.streamTools.activated) {
          this.postCheckpointStream();
        }

        // We need to do this even here for async, as we could be returning
        // data from an API endpoint, even if we were triggered async.
        if (this.options.createResponse) {
          data = await this.options.createResponse(jsonResponse(resultData));
        }

        // Terminal. `attachLazyOps` bundles any buffered ops with
        // `RunComplete` so the executor finalizes in one round-trip.
        return this.attachLazyOps(this.transformOutput({ data }));
      },

      /**
       * The user's function has thrown an error.
       */
      "function-rejected": async (checkpoint) => {
        const isFinal = !this.retriability(checkpoint.error);

        if (isFinal) {
          await this.streamCloseFailed(errorMessage(checkpoint.error));
        } else {
          // Retryable error — suppress the result event; the run will retry.
          await this.streamEnd();
        }

        if (!this.streamTools.activated) {
          this.postCheckpointStream();
        }

        // Buffered defer ops were recorded by user code that ran successfully
        // before the throw, so they should ship even when the function
        // ultimately errors. `attachLazyOps` bundles them alongside the
        // terminal error op (mirroring the `function-resolved` bundling for
        // `RunComplete`). The asyncCheckpointing rejection path achieves the
        // same intent via a final checkpoint call; pure-async has no
        // checkpoint channel, so we ship via the response body.
        return this.attachLazyOps(
          this.transformOutput({ error: checkpoint.error }),
        );
      },

      /**
       * We've found one or more steps. Here we may want to run a step or report
       * them back to Inngest.
       */
      "steps-found": async ({ steps }) => {
        const stepResult = await this.tryExecuteStep(steps);
        if (!stepResult) {
          return maybeReturnNewSteps();
        }

        // If the step's handler buffered any lazy ops (e.g. `defer` calls),
        // ship them alongside the step result instead of losing them in the
        // `step-ran` → single-op response.
        if (this.state.lazyOps.length === 0) {
          return stepRanHandler(stepResult);
        }

        const transformed = await stepRanHandler(stepResult);
        if (transformed.type !== "step-ran") {
          return transformed;
        }

        return this.attachLazyOps({
          type: "steps-found",
          ctx: transformed.ctx,
          ops: transformed.ops,
          steps: [transformed.step],
        });
      },

      /**
       * While trying to find a step that Inngest has told us to run, we've
       * timed out or have otherwise decided that it doesn't exist.
       */
      "step-not-found": ({ step }) => {
        const { foundSteps, totalFoundSteps } = this.getStepNotFoundDetails();
        return {
          type: "step-not-found",
          ctx: this.fnArg,
          ops: this.ops,
          step,
          foundSteps,
          totalFoundSteps,
        };
      },

      "checkpointing-runtime-reached": () => {
        throw new Error(
          "Checkpointing maximum runtime reached, but this is not in a checkpointing step mode. This is a bug in the Inngest SDK.",
        );
      },

      "checkpointing-buffer-interval-reached": () => {
        throw new Error(
          "Checkpointing maximum buffer interval reached, but this is not in a checkpointing step mode. This is a bug in the Inngest SDK.",
        );
      },
    };

    const asyncCheckpointingHandlers: CheckpointHandlers[StepMode.AsyncCheckpointing] =
      {
        "": commonCheckpointHandler,
        "function-resolved": async (checkpoint, i) => {
          // Capture buffered lazy ops up front so the underlying handler's
          // `maybeReturnNewSteps` doesn't ship them as a standalone
          // `steps-found` batch — we want to bundle them with the final
          // response (alongside `RunComplete` or any newly-discovered
          // real steps) so the executor doesn't need an extra round-trip.
          const lazyOps = this.state.lazyOps.drain();

          const output = await asyncHandlers["function-resolved"](
            checkpoint,
            i,
          );

          if (output?.type === "function-resolved") {
            const steps = [
              ...this.state.checkpointingStepBuffer,
              ...lazyOps,
              {
                op: StepOpCode.RunComplete,
                id: hashId(RUN_COMPLETE_STEP_ID),
                data: output.data,
              },
            ];

            if (isNonEmpty(steps)) {
              return {
                type: "steps-found",
                ctx: output.ctx,
                ops: output.ops,
                steps,
              };
            }
          }

          if (output?.type === "steps-found" && lazyOps.length) {
            return {
              ...output,
              steps: [...output.steps, ...lazyOps] as [
                OutgoingOp,
                ...OutgoingOp[],
              ],
            };
          }

          return output;
        },
        "function-rejected": async (checkpoint) => {
          // If we have buffered steps — either from step results or from
          // lazy ops (e.g. `defer` called before the error) — attempt
          // checkpointing them first. The buffered defer ops were recorded
          // by user code that ran successfully, so they should ship even
          // when the function ultimately errors.
          if (
            this.state.checkpointingStepBuffer.length ||
            this.state.lazyOps.length > 0
          ) {
            const fallback = await attemptCheckpointAndResume(
              undefined,
              false,
              true,
            );
            if (fallback) {
              return fallback;
            }
          }

          return await this.transformOutput({ error: checkpoint.error });
        },
        "step-not-found": asyncHandlers["step-not-found"],
        "steps-found": async ({ steps }) => {
          // Note that if we have a requested run step, we'll never be
          // checkpointing, as that's an async parallel execution mode.

          // Break found steps in to { stepsToResume, newSteps }
          const { stepsToResume, newSteps } = steps.reduce(
            (acc, step) => {
              if (!step.hasStepState) {
                acc.newSteps.push(step);
              } else if (!step.fulfilled) {
                acc.stepsToResume.push(step);
              }

              return acc;
            },
            { stepsToResume: [], newSteps: [] } as {
              stepsToResume: FoundStep[];
              newSteps: FoundStep[];
            },
          );

          this.devDebug("split found steps in to:", {
            stepsToResume: stepsToResume.length,
            newSteps: newSteps.length,
          });

          // Got new steps? Exit early.
          if (!this.options.requestedRunStep && newSteps.length) {
            // If the checkpointing runtime has been exceeded, don't execute new
            // steps in-process. Flush any buffered steps and return the new
            // steps to the executor so it can schedule them.
            if (this.state.checkpointingRuntimeExceeded) {
              if (this.state.checkpointingStepBuffer.length) {
                const fallback = await attemptCheckpointAndResume(
                  undefined,
                  false,
                  true,
                );
                if (fallback) {
                  return fallback;
                }
              }

              return maybeReturnNewSteps();
            }

            const stepResult = await this.tryExecuteStep(newSteps);
            if (stepResult) {
              this.devDebug(`executed step "${stepResult.id}" successfully`);

              // We executed a step!
              //
              // We know that because we're in this mode, we're always free to
              // checkpoint and continue if we ran a step and it was successful.
              if (stepResult.error) {
                // Flush buffered steps before falling back to async,
                // so previously-successful steps aren't lost.
                if (this.state.checkpointingStepBuffer.length) {
                  const fallback = await attemptCheckpointAndResume(
                    undefined,
                    false,
                    true,
                  );
                  if (fallback) {
                    return fallback;
                  }
                }

                return stepRanHandler(stepResult);
              }

              // If we're here, we successfully ran a step, so we may now need
              // to checkpoint it depending on the step buffer configured.
              return await attemptCheckpointAndResume(stepResult);
            }

            // Flush any buffered checkpoint steps before returning
            // new steps to the executor. Without this, steps executed
            // in-process during AsyncCheckpointing but not yet
            // checkpointed would be lost. When the executor later calls
            // back with requestedRunStep, those steps would be missing
            // from stepState, causing "step not found" errors.
            if (this.state.checkpointingStepBuffer.length) {
              const fallback = await attemptCheckpointAndResume(
                undefined,
                false,
                true,
              );
              if (fallback) {
                return fallback;
              }
            }

            return maybeReturnNewSteps();
          }

          // If we have stepsToResume, resume as many as possible and resume execution
          if (stepsToResume.length) {
            this.devDebug(`resuming ${stepsToResume.length} steps`);

            for (const st of stepsToResume) {
              this.resumeStepWithResult({
                ...st,
                id: st.hashedId,
              });
            }
          }

          return;
        },
        "checkpointing-runtime-reached": async () => {
          // Don't return a DiscoveryRequest immediately. Instead, set a flag so
          // that the next "steps-found" checkpoint returns the step to the
          // executor rather than executing it in-process. This lets user code
          // between steps run to completion and avoids re-entering the function
          // from scratch for stepless functions. In other words, it avoids
          // duplicate execution requests.
          this.state.checkpointingRuntimeExceeded = true;
          return undefined;
        },

        "checkpointing-buffer-interval-reached": () => {
          return attemptCheckpointAndResume(undefined, false, true);
        },
      };

    return {
      [StepMode.Async]: asyncHandlers,
      [StepMode.Sync]: syncHandlers,
      [StepMode.AsyncCheckpointing]: asyncCheckpointingHandlers,
    };
  }

  private getCheckpointHandler(type: keyof CheckpointHandlers[StepMode]) {
    return this.checkpointHandlers[this.options.stepMode][type] as (
      checkpoint: Checkpoint,
      iteration: number,
    ) => MaybePromise<ExecutionResult | undefined>;
  }

  private async tryExecuteStep(
    steps: FoundStep[],
  ): Promise<OutgoingOp | undefined> {
    const hashedStepIdToRun =
      this.options.requestedRunStep || this.getEarlyExecRunStep(steps);
    if (!hashedStepIdToRun) {
      return;
    }

    const step = steps.find(
      (step) => step.hashedId === hashedStepIdToRun && step.fn,
    );

    if (step) {
      return await this.executeStep(step);
    }

    /**
     * Ensure we reset the timeout if we have a requested run step but couldn't
     * find it, but also that we don't reset if we found and executed it.
     */
    return void this.timeout?.reset();
  }

  /**
   * Given a list of outgoing ops, decide if we can execute an op early and
   * return the ID of the step to execute if we can.
   */
  private getEarlyExecRunStep(steps: FoundStep[]): string | undefined {
    /**
     * We may have been disabled due to parallelism, in which case we can't
     * immediately execute unless explicitly requested.
     */
    if (this.options.disableImmediateExecution) return;

    const unfulfilledSteps = steps.filter((step) => !step.fulfilled);
    if (unfulfilledSteps.length !== 1) return;

    const op = unfulfilledSteps[0];

    if (
      op &&
      op.op === StepOpCode.StepPlanned
      // TODO We must individually check properties here that we do not want to
      // execute on, such as retry counts. Nothing exists here that falls in to
      // this case, but should be accounted for when we add them.
      // && typeof op.opts === "undefined"
    ) {
      return op.hashedId;
    }

    return;
  }

  private async filterNewSteps(
    foundSteps: FoundStep[],
  ): Promise<[OutgoingOp, ...OutgoingOp[]] | undefined> {
    if (this.options.requestedRunStep) {
      return;
    }

    const newSteps = foundSteps.reduce((acc, step) => {
      if (!step.hasStepState) {
        acc.push(step);
      }

      return acc;
    }, [] as FoundStep[]);

    if (!newSteps.length) {
      return;
    }

    await this.middlewareManager.onMemoizationEnd();

    const stepList = newSteps.map<OutgoingOp>((step) => {
      return {
        displayName: step.displayName,
        op: step.op,
        id: step.hashedId,
        name: step.name,
        opts: step.opts,
        userland: step.userland,
      };
    });

    if (!isNonEmpty(stepList)) {
      throw new UnreachableError("stepList is empty");
    }

    return stepList;
  }

  private async executeStep(foundStep: FoundStep): Promise<OutgoingOp> {
    const { id, name, opts, fn, displayName, userland, hashedId } = foundStep;
    const { stepInfo, wrappedHandler, setActualHandler } = foundStep.middleware;

    this.devDebug(`preparing to execute step "${id}"`);

    this.timeout?.clear();

    const outgoingOp: OutgoingOp = {
      id: hashedId,
      op: StepOpCode.StepRun,
      name,
      opts,
      displayName,
      userland,
    };
    this.state.executingStep = outgoingOp;

    const store = await getAsyncCtx();

    if (store?.execution) {
      store.execution.executingStep = {
        id,
        name: displayName,
        hashedId,
      };
    }

    this.devDebug(`executing step "${id}"`);

    if (this.rootSpanId && this.options.checkpointingConfig) {
      clientProcessorMap
        .get(this.options.client)
        ?.declareStepExecution(
          this.rootSpanId,
          userland.id ?? "",
          userland.index ?? 0,
          hashedId,
          this.options.data?.attempt ?? 0,
        );
    }

    let interval: GoInterval | undefined;

    // `fn` already has middleware-transformed args baked in via `fnArgs` (i.e.
    // the `transformStepInput` middleware hook already ran).
    const actualHandler = () => runAsPromise(fn);

    await this.middlewareManager.onMemoizationEnd();
    await this.middlewareManager.onStepStart(stepInfo);

    // If wrappedHandler hasn't been called yet (no deferred from discovery),
    // set one up so wrapStep's next() still blocks until memoization.
    if (!foundStep.memoizationDeferred) {
      const deferred = createDeferredPromise<unknown>();
      foundStep.memoizationDeferred = deferred;
      setActualHandler(() => deferred.promise);
      foundStep.transformedResultPromise = wrappedHandler();
      foundStep.transformedResultPromise.catch(() => {
        // Swallow — errors handled by handle()
      });
    }

    // Build wrapStepHandler chain around the actual handler
    const wrappedActualHandler =
      this.middlewareManager.buildWrapStepHandlerChain(actualHandler, stepInfo);

    return goIntervalTiming(() => wrappedActualHandler())
      .finally(() => {
        this.devDebug(`finished executing step "${id}"`);

        this.state.executingStep = undefined;

        if (this.rootSpanId && this.options.checkpointingConfig) {
          clientProcessorMap
            .get(this.options.client)
            ?.clearStepExecution(this.rootSpanId);
        }

        if (store?.execution) {
          delete store.execution.executingStep;
        }
      })
      .then<OutgoingOp>(async ({ resultPromise, interval: _interval }) => {
        interval = _interval;
        const metadata = this.state.metadata?.get(id);
        const serverData = await resultPromise;

        // Don't resolve memoizationDeferred here. wrapStep's next() must
        // block until the step is actually memoized (i.e. handle() fires
        // with confirmed data from the server). handle() resolves it.
        await this.middlewareManager.onStepComplete(stepInfo, serverData);

        // Emit inngest.commit — step data is finalized.
        this.streamTools.commit(hashedId);

        return {
          ...outgoingOp,
          data: serverData,
          ...(metadata && metadata.length > 0 ? { metadata: metadata } : {}),
        };
      })
      .catch<OutgoingOp>((error) => {
        // Don't reject memoizationDeferred — handle() will reject it when
        // the error is memoized.
        return this.buildStepErrorOp({
          error,
          id,
          outgoingOp,
          stepInfo,
        });
      })
      .then((op) => ({
        ...op,
        timing: interval,
      }));
  }

  /**
   * Starts execution of the user's function, including triggering checkpoints
   * and middleware hooks where appropriate.
   */
  private async startExecution(): Promise<void> {
    /**
     * Start the timer to time out the run if needed.
     */
    void this.timeout?.start();
    void this.checkpointingMaxRuntimeTimer?.start();
    void this.checkpointingMaxBufferIntervalTimer?.start();

    const fnInputResult = await this.middlewareManager.transformFunctionInput();
    this.applyFunctionInputMutations(fnInputResult);

    if (this.state.allStateUsed()) {
      await this.middlewareManager.onMemoizationEnd();
    }

    if (this.state.stepsToFulfill === 0 && this.fnArg.attempt === 0) {
      await this.middlewareManager.onRunStart();
    }

    const innerHandler: () => Promise<unknown> = async () => {
      await this.validateEventSchemas();
      return this.userFnToRun(this.fnArg);
    };

    const runHandler = this.middlewareManager.wrapRunHandler(innerHandler);

    runAsPromise(runHandler)
      .then(async (data) => {
        await this.middlewareManager.onRunComplete(data);
        this.state.setCheckpoint({ type: "function-resolved", data });
      })
      .catch(async (error) => {
        // Preserve Error instances; stringify non-Error throws (e.g. `throw {}`)
        let err: Error;
        if (error instanceof Error) {
          err = error;
        } else if (typeof error === "object") {
          err = new Error(JSON.stringify(error));
        } else {
          err = new Error(String(error));
        }

        await this.middlewareManager.onRunError(err, !this.retriability(err));
        this.state.setCheckpoint({ type: "function-rejected", error: err });
      });
  }

  /**
   * Determine whether the given error is retriable. Returns `false` when the
   * run should not be retried, a duration string for `RetryAfterError`, or
   * `true` for normal retry behavior.
   */
  private retriability(error: unknown): boolean | string {
    const areRetriesExhausted =
      this.fnArg.maxAttempts &&
      this.fnArg.maxAttempts - 1 === this.fnArg.attempt;
    if (areRetriesExhausted) {
      return false;
    }

    // TODO: Replace this fragile inheritance + name check. Maybe we should have
    // an "~inngest" field with an object that specifies "isRetriable".
    if (
      error instanceof NonRetriableError ||
      // biome-ignore lint/suspicious/noExplicitAny: instanceof fails across module boundaries
      (error as any)?.name === "NonRetriableError"
    ) {
      return false;
    }

    // If the function-level code did not change the error, then we don't want
    // to retry. The vast majority of the time this means there wasn't a `catch`
    // block.
    const isUncaughtStepError =
      error instanceof StepError &&
      error === this.state.recentlyRejectedStepError;
    if (isUncaughtStepError) {
      return false;
    }

    // TODO: Replace this fragile inheritance + name check. Maybe we should have
    // an "~inngest" field with an object that specifies "retryAfter".
    if (
      error instanceof RetryAfterError ||
      // biome-ignore lint/suspicious/noExplicitAny: instanceof fails across module boundaries
      (error as any)?.name === "RetryAfterError"
    ) {
      return (error as RetryAfterError).retryAfter;
    }

    return true;
  }

  /**
   * Build the OutgoingOp for a failed step, notifying middleware and choosing
   * retriable vs non-retriable opcode.
   */
  private async buildStepErrorOp({
    error,
    id,
    outgoingOp,
    stepInfo,
  }: {
    error: unknown;
    id: string;
    outgoingOp: OutgoingOp;
    stepInfo: Middleware.StepInfo;
  }): Promise<OutgoingOp> {
    const isFinal = !this.retriability(error);
    const metadata = this.state.metadata?.get(id);

    await this.middlewareManager.onStepError(
      stepInfo,
      error instanceof Error ? error : new Error(String(error)),
      isFinal,
    );

    // Step's stream should be rolled back
    this.streamTools.rollback(outgoingOp.id);

    // Serialize the error so it survives JSON.stringify (raw Error
    // objects have non-enumerable properties that get dropped).
    // This is critical for checkpoint requests where the error is
    // sent as JSON in the request body.
    const serialized = serializeError(error);

    return {
      ...outgoingOp,
      error: serialized,
      op: isFinal ? StepOpCode.StepFailed : StepOpCode.StepError,
      ...(metadata && metadata.length > 0 ? { metadata } : {}),
    };
  }

  /**
   * Validate event data against schemas defined in function triggers.
   */
  private async validateEventSchemas(): Promise<void> {
    if (this.options.handlerKind === "failure") {
      // The failure handler is triggered by "inngest/function.failed" —
      // the main function's trigger schemas don't apply.
      return;
    }

    if (this.options.handlerKind === "defer") {
      // Validate the incoming event.data against the defer handler's own
      // schema. This catches type mismatches from serialization (e.g. a
      // Date that becomes an ISO string over the wire).
      await this.validateDeferEventSchema();
      return;
    }

    const triggers = this.options.fn.opts.triggers;
    if (!triggers || triggers.length === 0) return;

    const fnArgEvents = this.fnArg.events;
    if (!fnArgEvents || fnArgEvents.length === 0) return;

    const events = fnArgEvents.map((event) => ({
      name: event.name,
      data: event.data,
    }));

    await validateEvents(events, triggers);
  }

  /**
   * Validate the deferred event's data against the defer function's own
   * schema (set via `createDefer`'s `opts.schema`).
   */
  private async validateDeferEventSchema(): Promise<void> {
    const fn = this.options.fn;
    if (!isDeferredFunction(fn) || !fn.schema) {
      return;
    }

    const eventData = this.fnArg.event?.data;
    const result = await fn.schema["~standard"].validate(eventData);
    if (result.issues) {
      // Fail without retries. The event data won't change so there's no point
      // in retrying. This matches what we do for normal triggers.
      throw new NonRetriableError(
        `defer handler "${fn.id(this.options.client.id)}" schema validation failed: ${JSON.stringify(result.issues)}`,
      );
    }
  }

  /**
   * Using middleware, transform output before returning.
   */
  private transformOutput(dataOrError: {
    data?: unknown;
    error?: unknown;
  }): ExecutionResult {
    const { data, error } = dataOrError;

    if (typeof error !== "undefined") {
      const retriable = this.retriability(error);
      const serializedError = serializeError(error);

      return {
        type: "function-rejected",
        ctx: this.fnArg,
        ops: this.ops,
        error: serializedError,
        retriable,
      };
    }

    return {
      type: "function-resolved",
      ctx: this.fnArg,
      ops: this.ops,
      data: undefinedToNull(data),
    };
  }

  /**
   * Drain buffered lazy ops (e.g. `DeferAdd` from `defer()`) and merge them
   * into `result` so they ship in the same outbound message. Lazy ops are
   * fire-and-forget and have no natural shipping moment, so each terminal code
   * path must ship them or they're silently dropped.
   */
  private attachLazyOps(
    result: ExecutionResult,
    extras: readonly OutgoingOp[] = [],
  ): ExecutionResult {
    const lazyOps = this.state.lazyOps.drain();
    if (lazyOps.length === 0 && extras.length === 0) {
      return result;
    }

    switch (result.type) {
      // Convert terminal results into `steps-found` so the lazy ops ride
      // alongside `RunComplete` / the terminal error op. This lets the executor
      // finalize in one round-trip instead of replaying just to receive
      // terminal data.
      case "function-resolved": {
        const steps: OutgoingOp[] = [
          ...extras,
          ...lazyOps,
          {
            op: StepOpCode.RunComplete,
            id: hashId(RUN_COMPLETE_STEP_ID),
            data: undefinedToNull(result.data),
          },
        ];
        return {
          type: "steps-found",
          ctx: result.ctx,
          ops: result.ops,
          steps: steps as [OutgoingOp, ...OutgoingOp[]],
        };
      }

      case "function-rejected": {
        const isFinal = result.retriable === false;
        const steps: OutgoingOp[] = [
          ...extras,
          ...lazyOps,
          {
            op: isFinal ? StepOpCode.StepFailed : StepOpCode.StepError,
            id: hashId(RUN_COMPLETE_STEP_ID),
            error: result.error,
          },
        ];
        return {
          type: "steps-found",
          ctx: result.ctx,
          ops: result.ops,
          steps: steps as [OutgoingOp, ...OutgoingOp[]],
        };
      }

      // Already a multi-op batch; just append.
      case "steps-found": {
        return {
          ...result,
          steps: [...result.steps, ...extras, ...lazyOps] as [
            OutgoingOp,
            ...OutgoingOp[],
          ],
        };
      }

      // `step-ran` / `step-not-found` / `change-mode` carry a single op or no
      // ops; there's no slot to attach lazy ops or extras. Re-buffer the
      // drained ops so they ship on the next outbound message.
      default:
        for (const op of lazyOps) {
          this.state.lazyOps.push(op);
        }
        return result;
    }
  }

  private createExecutionState(): ExecutionState {
    const d = createDeferredPromiseWithStack<Checkpoint>();
    let checkpointResolve = d.deferred.resolve;
    const checkpointResults = d.results;

    const loop: ExecutionState["loop"] = (async function* (
      cleanUp?: () => void,
    ) {
      try {
        while (true) {
          const res = (await checkpointResults.next()).value;
          if (res) {
            yield res;
          }
        }
      } finally {
        cleanUp?.();
      }
    })(() => {
      this.timeout?.clear();
      this.checkpointingMaxRuntimeTimer?.clear();
      this.checkpointingMaxBufferIntervalTimer?.clear();
      void checkpointResults.return();
    });

    const stepsToFulfill = Object.keys(this.options.stepState).length;

    const state: ExecutionState = {
      stepState: this.options.stepState,
      priorDefers: this.options.priorDefers ?? {},
      stepsToFulfill,
      steps: new Map(),
      loop,
      hasSteps: Boolean(stepsToFulfill),
      stepCompletionOrder: [...this.options.stepCompletionOrder],
      remainingStepsToBeSeen: new Set(this.options.stepCompletionOrder),
      setCheckpoint: (checkpoint: Checkpoint) => {
        this.devDebug("setting checkpoint:", checkpoint.type);

        ({ resolve: checkpointResolve } = checkpointResolve(checkpoint));
      },
      allStateUsed: () => {
        return this.state.remainingStepsToBeSeen.size === 0;
      },
      checkpointingStepBuffer: [],
      lazyOps: new LazyOps(),
      metadata: new Map(),
    };

    return state;
  }

  get ops(): Record<string, MemoizedOp> {
    return Object.fromEntries(this.state.steps);
  }

  private createFnArg(): Context.Any {
    const { step, defer } = this.createStepTools();
    const experimentStepRun = (step as unknown as ExperimentStepTools)[
      experimentStepRunSymbol
    ];

    let fnArg = {
      ...(this.options.data as { event: EventPayload }),
      step,
      group: createGroupTools({ experimentStepRun }),
      defer,
    } as unknown as Context.Any;

    if (this.options.handlerKind === "defer") {
      // Delete our internal metadata. The user's handler shouldn't see it since
      // it's an implementation detail
      delete fnArg.event.data._inngest;
      for (const event of fnArg.events) {
        delete event.data._inngest;
      }
    }

    /**
     * Handle use of the `onFailure` option by deserializing the error.
     */
    if (this.options.handlerKind === "failure") {
      const eventData = z
        .object({ error: jsonErrorSchema })
        .parse(fnArg.event?.data);

      (fnArg as Partial<Pick<FailureEventArgs, "error">>) = {
        ...fnArg,
        error: deserializeError(eventData.error),
      };
    }

    return this.options.transformCtx?.(fnArg) ?? fnArg;
  }

  /**
   * Apply mutations from `transformFunctionInput` back to execution state.
   * Allows middleware to modify event data, step tools, memoized step data,
   * and inject custom fields into the handler context.
   */
  private applyFunctionInputMutations(
    result: Middleware.TransformFunctionInputArgs,
  ): void {
    const { event, events, step, ...extensions } = result.ctx;

    // Mutate in place so the ALS store's reference to this.fnArg stays valid.
    if (event !== this.fnArg.event) {
      this.fnArg.event = event;
    }

    if (events !== this.fnArg.events) {
      this.fnArg.events = events;
    }

    if (step !== this.fnArg.step) {
      this.fnArg.step = step;
    }

    if (Object.keys(extensions).length > 0) {
      Object.assign(this.fnArg, extensions);
    }

    // Apply step data mutations
    for (const [hashedId, stepData] of Object.entries(result.steps)) {
      const existing = this.state.stepState[hashedId];
      if (
        existing &&
        stepData &&
        stepData.type === "data" &&
        stepData.data !== existing.data
      ) {
        this.state.stepState[hashedId] = { ...existing, data: stepData.data };
      }
    }
  }

  private createStepTools(): {
    step: ReturnType<typeof createStepTools>;
    defer: DeferFn;
  } {
    /**
     * A list of steps that have been found and are being rolled up before being
     * reported to the core loop.
     */
    const foundStepsToReport: Map<string, FoundStep> = new Map();

    /**
     * A map of the subset of found steps to report that have not yet been
     * handled. Used for fast access to steps that need to be handled in order.
     */
    const unhandledFoundStepsToReport: Map<string, FoundStep> = new Map();

    /**
     * A map of the latest sequential step indexes found for each step ID. Used
     * to ensure that we don't index steps in parallel.
     *
     * Note that these must be sequential; if we've seen or assigned `a:1`,
     * `a:2` and `a:4`, the latest sequential step index is `2`.
     *
     */
    const expectedNextStepIndexes: Map<string, number> = new Map();

    /**
     * An ordered list of step IDs that have yet to be handled in this
     * execution. Used to ensure that we handle steps in the order they were
     * found and based on the `stepCompletionOrder` in this execution's state.
     */
    const remainingStepCompletionOrder: string[] =
      this.state.stepCompletionOrder.slice();

    /**
     * A promise that's used to ensure that step reporting cannot be run more than
     * once in a given asynchronous time span.
     */
    let foundStepsReportPromise: Promise<void> | undefined;

    /**
     * A flag used to ensure that we only warn about parallel indexing once per
     * execution to avoid spamming the console.
     */
    let warnOfParallelIndexing = false;

    /**
     * Counts the number of times we've extended this tick.
     */
    let tickExtensionCount = 0;

    /**
     * Given a colliding step ID, maybe warn the user about parallel indexing.
     */
    const maybeWarnOfParallelIndexing = (userlandCollisionId: string) => {
      if (warnOfParallelIndexing) {
        return;
      }

      const hashedCollisionId = _internals.hashId(userlandCollisionId);

      const stepExists = this.state.steps.has(hashedCollisionId);
      if (stepExists) {
        const stepFoundThisTick = foundStepsToReport.has(hashedCollisionId);
        if (!stepFoundThisTick) {
          warnOfParallelIndexing = true;

          this.options.client["warnMetadata"](
            { run_id: this.fnArg.runId },
            ErrCode.AUTOMATIC_PARALLEL_INDEXING,
            {
              message: `Duplicate step ID "${userlandCollisionId}" detected across parallel chains`,
              explanation:
                "Using the same ID for steps in different parallel chains can cause unexpected behaviour. Your function is still running.",
              action:
                "Use a unique ID for each step, especially those in parallel.",
              code: ErrCode.AUTOMATIC_PARALLEL_INDEXING,
            },
          );
        }
      }
    };

    /**
     * A helper used to report steps to the core loop. Used after adding an item
     * to `foundStepsToReport`.
     */
    const reportNextTick = () => {
      // Being explicit instead of using `??=` to appease TypeScript.
      if (foundStepsReportPromise) {
        return;
      }

      let extensionPromise: Promise<void>;
      if (++tickExtensionCount >= 10) {
        tickExtensionCount = 0;
        extensionPromise = resolveNextTick();
      } else {
        extensionPromise = resolveAfterPending();
      }

      foundStepsReportPromise = extensionPromise.then(() => {
        foundStepsReportPromise = undefined;

        for (let i = 0; i < remainingStepCompletionOrder.length; i++) {
          const nextStepId = remainingStepCompletionOrder[i];
          if (!nextStepId) {
            // Strange - skip this empty index
            continue;
          }

          const handled = unhandledFoundStepsToReport.get(nextStepId)?.handle();
          if (handled) {
            remainingStepCompletionOrder.splice(i, 1);
            unhandledFoundStepsToReport.delete(nextStepId);
            return void reportNextTick();
          }
        }

        // If we've handled no steps in this "tick," roll up everything we've
        // found and report it.
        const steps = [...foundStepsToReport.values()];
        foundStepsToReport.clear();
        unhandledFoundStepsToReport.clear();

        if (!isNonEmpty(steps)) {
          return;
        }

        return void this.state.setCheckpoint({
          type: "steps-found",
          steps: steps,
        });
      });
    };

    /**
     * A helper used to push a step to the list of steps to report.
     */
    const pushStepToReport = (step: FoundStep) => {
      foundStepsToReport.set(step.hashedId, step);
      unhandledFoundStepsToReport.set(step.hashedId, step);
      reportNextTick();
    };

    const stepHandler: StepHandler = async ({
      args,
      matchOp,
      opts,
    }): Promise<unknown> => {
      const stepOptions = getStepOptions(args[0]);
      const opId = matchOp(stepOptions, ...args.slice(1));

      // Opcode-only sync ops (e.g. `DeferAdd`) are fire-and-forget and
      // not user-addressable as steps: they have no handler, no
      // memoization, and shouldn't fire any step middleware (`wrapStep`,
      // `transformStepInput`, `onStepStart`, etc.). Buffer the op for
      // lazy shipment and return; the core loop, middleware pipeline,
      // and `state.steps` are all bypassed. See `ARCHITECTURE.md`.
      if (isLazyOp(opts, opId)) {
        const hashedId = _internals.hashId(opId.id);
        if (this.state.lazyOps.hasId(hashedId)) {
          // defer.md: "defer IDs must be unique within a run". Skip the
          // duplicate and warn so the user can spot the bug — silently
          // shipping two ops with identical hashed ids relies on backend
          // dedup and hides the mistake.
          this.options.client[internalLoggerSymbol].warn(
            { runId: this.fnArg.runId, id: opId.userland?.id ?? opId.id },
            "defer skipped: duplicate ID within run",
          );
          return;
        }
        if (this.state.priorDefers[hashedId]) {
          // Replay: this id was already shipped in a prior execution.  Silently
          // skip, but mark it as seen so a later call with the same id (a real
          // duplicate) is detected and warned about above.
          this.state.lazyOps.markSeen(hashedId);
          return;
        }
        this.state.lazyOps.push({
          id: hashedId,
          op: opId.op,
          name: opId.name,
          displayName: opId.displayName ?? opId.id,
          opts: opId.opts,
          userland: opId.userland,
          data: null,
        });
        return;
      }

      if (this.state.executingStep) {
        /**
         * If a step is found after asynchronous actions during another step's
         * execution, everything is fine. The problem here is if we've found
         * that a step nested inside another a step, which is something we don't
         * support at the time of writing.
         *
         * In this case, we could use something like Async Hooks to understand
         * how the step is being triggered, though this isn't available in all
         * environments.
         *
         * Therefore, we'll only show a warning here to indicate that this is
         * potentially an issue.
         */
        this.options.client["warnMetadata"](
          { run_id: this.fnArg.runId },
          ErrCode.NESTING_STEPS,
          {
            message: `Nested step tooling detected in "${opId.displayName ?? opId.id}"`,
            explanation:
              "Nesting step.* calls is not supported. This warning may also appear if steps are separated by regular async calls, which is fine.",
            action:
              "Avoid using step.* inside other step.* calls. Use a separate async function or promise chaining to compose steps.",
            code: ErrCode.NESTING_STEPS,
          },
        );
      }

      // Apply middleware transformations (may change step ID, affecting
      // memoization lookup)
      const {
        hashedId,
        isFulfilled,
        setActualHandler,
        stepInfo,
        stepState,
        wrappedHandler,
      } = await this.applyMiddlewareToStep(
        opId,
        expectedNextStepIndexes,
        maybeWarnOfParallelIndexing,
      );

      const { promise, resolve, reject } = createDeferredPromise();

      let extraOpts: Record<string, unknown> | undefined;
      let fnArgs = [...args];

      if (
        typeof stepState?.input !== "undefined" &&
        Array.isArray(stepState.input)
      ) {
        switch (opId.op) {
          // `step.run()` has its function input affected
          case StepOpCode.StepPlanned: {
            fnArgs = [...args.slice(0, 2), ...stepState.input];

            extraOpts = { input: [...stepState.input] };
            break;
          }

          // `step.ai.infer()` has its body affected
          case StepOpCode.AiGateway: {
            extraOpts = {
              body: {
                ...(typeof opId.opts?.body === "object"
                  ? { ...opId.opts.body }
                  : {}),
                ...stepState.input[0],
              },
            };
            break;
          }
        }
      }

      // If transformStepInput middleware may have changed the input, update
      // `fnArgs` so `fn` uses the transformed values. Skip if replay already
      // set `extraOpts`.
      if (!extraOpts && Array.isArray(stepInfo.input)) {
        fnArgs = [...args.slice(0, 2), ...stepInfo.input];
      }

      const step: FoundStep = {
        ...opId,
        opts: { ...opId.opts, ...extraOpts },
        rawArgs: fnArgs,
        hashedId,
        input: stepState?.input,

        fn: opts?.fn ? () => opts.fn?.(this.fnArg, ...fnArgs) : undefined,
        promise,
        fulfilled: isFulfilled,
        hasStepState: Boolean(stepState),
        displayName: opId.displayName ?? opId.id,
        handled: false,

        // Middleware context for deferred handler pattern
        middleware: {
          wrappedHandler,
          stepInfo,
          setActualHandler,
        },

        handle: () => {
          if (step.handled) {
            return false;
          }

          this.devDebug(`handling step "${hashedId}"`);

          step.handled = true;

          // Refetch step state because it may have been changed since we found
          // the step. This could be due to checkpointing, where we run this
          // live and then return to the function.
          const result = this.state.stepState[hashedId];

          if (step.fulfilled && result) {
            result.fulfilled = true;

            // For some execution scenarios such as testing, `data`, `error`,
            // and `input` may be `Promises`. This could also be the case for
            // future middleware applications. For this reason, we'll make sure
            // the values are fully resolved before continuing.
            void Promise.all([result.data, result.error, result.input]).then(
              async () => {
                // If the wrapStep chain already ran during discovery in this
                // same request (checkpointing), reuse its result instead of
                // firing wrappedHandler() again. This prevents middleware from
                // seeing a duplicate wrapStep call per step per request.
                if (step.transformedResultPromise) {
                  // Resolve the memoization deferred so wrapStep's next()
                  // unblocks. The step data is now confirmed memoized.
                  if (step.memoizationDeferred) {
                    if (typeof result.data !== "undefined") {
                      step.memoizationDeferred.resolve(await result.data);
                    } else {
                      const stepError = new StepError(opId.id, result.error);
                      this.state.recentlyRejectedStepError = stepError;
                      step.memoizationDeferred.reject(stepError);
                    }
                  }

                  step.transformedResultPromise.then(resolve, reject);
                  return;
                }

                // The wrapStep chain is about to fire again to resolve the
                // step promise through middleware (e.g. deserialization).
                // Mark the step as memoized so middleware can distinguish
                // this from the original execution call.
                //
                // This need for this change was discovered when checkpointing +
                // middleware's "double `wrapStep` call" behavior had `memoized:
                // false` on the 2nd call
                step.middleware.stepInfo.memoized = true;

                if (typeof result.data !== "undefined") {
                  // Validate waitForEvent results against the schema if present
                  // Skip validation if result.data is null (timeout case)
                  if (
                    opId.op === StepOpCode.WaitForEvent &&
                    result.data !== null
                  ) {
                    const { event } = (step.rawArgs?.[1] ?? {}) as {
                      event: unknown;
                    };
                    if (!event) {
                      // Unreachable
                      throw new Error("Missing event option in waitForEvent");
                    }
                    try {
                      await validateEvents(
                        [result.data],

                        // @ts-expect-error - This is a full event object at runtime
                        [{ event }],
                      );
                    } catch (err) {
                      this.state.recentlyRejectedStepError = new StepError(
                        opId.id,
                        err,
                      );
                      reject(this.state.recentlyRejectedStepError);
                      return;
                    }
                  }

                  // Set inner handler to return memoized data
                  step.middleware.setActualHandler(() =>
                    Promise.resolve(result.data),
                  );

                  step.middleware.wrappedHandler().then(resolve);
                } else {
                  const stepError = new StepError(opId.id, result.error);
                  this.state.recentlyRejectedStepError = stepError;

                  // Set inner handler to reject with step error
                  step.middleware.setActualHandler(() =>
                    Promise.reject(stepError),
                  );

                  step.middleware.wrappedHandler().catch(reject);
                }
              },
            );
          }

          return true;
        },
      };

      this.state.steps.set(hashedId, step);
      this.state.hasSteps = true;

      const isNewStepWithHandler = !isFulfilled && !stepState && step.fn;
      if (isNewStepWithHandler) {
        // New, never-seen step with a handler (e.g. `step.run`). Kick off the
        // middleware wrapStep chain now so it runs during discovery, not later
        // in executeStep.
        //
        // This is necessary so that middleware can inject their own steps.
        // Reporting is deferred to the center of the onion so that if
        // middleware throws or injects prerequisites, the step is never
        // reported.
        const deferred = createDeferredPromise<unknown>();
        step.memoizationDeferred = deferred;

        setActualHandler(() => {
          pushStepToReport(step);
          return deferred.promise;
        });

        step.transformedResultPromise = wrappedHandler();
        step.transformedResultPromise.catch((error) => {
          reject(error);
        });
      } else {
        pushStepToReport(step);
      }

      return promise;
    };

    const baseTools = createStepTools(this.options.client, this, stepHandler);
    const defer = this.buildDefer(stepHandler);

    return { step: baseTools, defer };
  }

  /**
   * Build the `defer(idOrOptions, { function, data })` method exposed on
   * every handler context. Validates `data` against the target function's
   * schema (if any) and emits a `DeferAdd` opcode that routes to the
   * target's companion function slug.
   *
   * `defer()` is fire-and-forget: a misuse should not derail the user's
   * handler. Validation failures (wrong function, async schema validator,
   * schema mismatch) are logged and the call is silently skipped.
   */
  private buildDefer(stepHandler: StepHandler): DeferFn {
    return (idOrOptions, { function: deferFn, data }) => {
      const log = this.options.client[internalLoggerSymbol];
      const runId = this.fnArg.runId;

      try {
        if (!isDeferredFunction(deferFn)) {
          log.error(
            { runId },
            "defer skipped: function not created via createDefer",
          );
          return;
        }

        const { schema } = deferFn;
        const deferFnSlug = deferFn.id(this.options.client.id);

        let input: unknown = data;
        if (schema) {
          const result = schema["~standard"].validate(data);
          if (result instanceof Promise) {
            log.error(
              { runId },
              "defer() requires a synchronous schema validator. The defer call was skipped.",
            );
            return;
          }
          if (result.issues) {
            log.error(
              { runId, issues: result.issues },
              "defer skipped: schema validation failed",
            );
            return;
          }
          input = result.value ?? data;
        }

        void stepHandler({
          args: [idOrOptions, input],
          matchOp: (stepOptions, inputArg) => ({
            id: stepOptions.id,
            mode: StepMode.Sync,
            op: StepOpCode.DeferAdd,
            name: stepOptions.name ?? stepOptions.id,
            displayName: stepOptions.name ?? stepOptions.id,
            opts: { fn_slug: deferFnSlug, input: inputArg },
            userland: { id: stepOptions.id },
          }),
        }).catch((err: unknown) => {
          log.error({ runId, err }, "defer skipped: unexpected error");
        });
      } catch (err) {
        // Fire-and-forget: a misbehaving schema validator, malformed args, or
        // any other unexpected throw must not derail the surrounding handler.
        log.error({ runId, err }, "defer skipped: unexpected error");
      }
    };
  }

  /**
   * Applies middleware transformations to a step, resolves ID collisions,
   * and performs memoization lookup.
   */
  private async applyMiddlewareToStep(
    opId: HashedOp,
    expectedNextStepIndexes: Map<string, number>,
    maybeWarnOfParallelIndexing: (userlandCollisionId: string) => void,
  ): Promise<MiddlewareApplicationResult> {
    // 1. Resolve initial collision with original ID
    const initialCollision = resolveStepIdCollision({
      baseId: opId.id,
      expectedIndexes: expectedNextStepIndexes,
      stepsMap: this.state.steps,
    });
    if (initialCollision.finalId !== opId.id) {
      maybeWarnOfParallelIndexing(opId.id);
      opId.id = initialCollision.finalId;
      if (initialCollision.index !== undefined) {
        opId.userland.index = initialCollision.index;
      }
    }

    const originalId = opId.userland.id;
    let hashedId = _internals.hashId(opId.id);

    // 2. Apply middleware (stepType, input extraction, deferred handler).
    //    Pass preliminary memoization status so middleware sees it.
    const prepared = await this.middlewareManager.applyToStep({
      displayName: opId.displayName ?? opId.userland.id,
      hashedId,
      memoized:
        Boolean(this.state.stepState[hashedId]) &&
        typeof this.state.stepState[hashedId]?.input === "undefined",
      op: opId.op,
      opts: opId.opts,
      userlandId: opId.userland.id,
    });
    const { entryPoint, opName, opOpts, setActualHandler, stepInfo } = prepared;

    if (opName !== undefined) {
      opId.name = opName;
    }
    if (opOpts !== undefined) {
      opId.opts = opOpts;
    }

    // 3. If middleware changed the step ID, re-resolve collisions
    if (stepInfo.options.id !== originalId) {
      opId.id = stepInfo.options.id;
      opId.userland.id = stepInfo.options.id;

      const secondCollision = resolveStepIdCollision({
        baseId: stepInfo.options.id,
        expectedIndexes: expectedNextStepIndexes,
        stepsMap: this.state.steps,
      });
      if (secondCollision.finalId !== stepInfo.options.id) {
        opId.id = secondCollision.finalId;
        opId.userland.id = secondCollision.finalId;
        stepInfo.options.id = secondCollision.finalId;
        if (secondCollision.index !== undefined) {
          opId.userland.index = secondCollision.index;
        }
      }

      // Recompute hashedId with final ID
      hashedId = _internals.hashId(opId.id);
      stepInfo.hashedId = hashedId;
    }

    // 4. Final memoization lookup with potentially modified hashedId.
    //    Also marks step as seen and may trigger onMemoizationEnd.
    const stepState = this.state.stepState[hashedId];
    let isFulfilled = false;
    if (stepState) {
      stepState.seen = true;
      this.state.remainingStepsToBeSeen.delete(hashedId);

      if (this.state.allStateUsed()) {
        await this.middlewareManager.onMemoizationEnd();
      }

      if (typeof stepState.input === "undefined") {
        isFulfilled = true;
      }
      stepInfo.memoized = isFulfilled;
    } else {
      stepInfo.memoized = false;
    }

    // 5. Build wrapStep chain after all mutations so middleware sees final values
    const wrappedHandler = this.middlewareManager.buildWrapStepChain(
      entryPoint,
      stepInfo,
    );

    return {
      hashedId,
      stepInfo,
      wrappedHandler,
      setActualHandler,
      stepState,
      isFulfilled,
    };
  }

  private resumeStepWithResult(resultOp: OutgoingOp, resume = true): FoundStep {
    const userlandStep = this.state.steps.get(resultOp.id);
    if (!userlandStep) {
      throw new Error(
        "Step not found in memoization state during async checkpointing; this should never happen and is a bug in the Inngest SDK",
      );
    }

    const data = undefinedToNull(resultOp.data);

    userlandStep.data = data;
    userlandStep.timing = resultOp.timing;
    userlandStep.op = resultOp.op;
    userlandStep.id = resultOp.id;
    userlandStep.metadata = resultOp.metadata;

    if (resume) {
      userlandStep.fulfilled = true;
      userlandStep.hasStepState = true;
      this.state.stepState[resultOp.id] = userlandStep;

      userlandStep.handle();
    }

    return userlandStep;
  }

  private getUserFnToRun(): Handler.Any {
    switch (this.options.handlerKind) {
      case "defer":
        // Defer functions are real InngestFunctions — the handler is stored
        // in the same slot as a main function's handler.
        return this.options.fn["fn"];

      case "failure": {
        if (!this.options.fn["onFailureFn"]) {
          /**
           * Somehow, we've ended up detecting that this is a failure handler
           * but doesn't have an `onFailure` function. This should never
           * happen.
           */
          throw new Error("Cannot find function `onFailure` handler");
        }

        return this.options.fn["onFailureFn"];
      }

      default:
        return this.options.fn["fn"];
    }
  }

  private initializeTimer(state: ExecutionState): void {
    if (!this.options.requestedRunStep) {
      return;
    }

    this.timeout = createTimeoutPromise(this.timeoutDuration);

    void this.timeout.then(async () => {
      await this.middlewareManager.onMemoizationEnd();
      const { foundSteps, totalFoundSteps } = this.getStepNotFoundDetails();
      state.setCheckpoint({
        type: "step-not-found",
        step: {
          id: this.options.requestedRunStep as string,
          op: StepOpCode.StepNotFound,
        },
        foundSteps,
        totalFoundSteps,
      });
    });
  }

  private getStepNotFoundDetails(): {
    foundSteps: BasicFoundStep[];
    totalFoundSteps: number;
  } {
    const foundSteps = [...this.state.steps.values()]
      .filter((step) => !step.hasStepState)
      .map<BasicFoundStep>((step) => ({
        id: step.hashedId,
        name: step.name,
        displayName: step.displayName,
      }))
      .sort((a, b) => a.id.localeCompare(b.id));

    return {
      foundSteps: foundSteps.slice(0, STEP_NOT_FOUND_MAX_FOUND_STEPS),
      totalFoundSteps: foundSteps.length,
    };
  }

  private initializeCheckpointRuntimeTimer(state: ExecutionState): void {
    this.devDebug(
      "initializing checkpointing runtime timers",
      this.options.checkpointingConfig,
    );

    if (this.options.checkpointingConfig?.maxRuntime) {
      const maxRuntimeMs = Temporal.isTemporalDuration(
        this.options.checkpointingConfig.maxRuntime,
      )
        ? this.options.checkpointingConfig.maxRuntime.total({
            unit: "milliseconds",
          })
        : typeof this.options.checkpointingConfig.maxRuntime === "string"
          ? ms(this.options.checkpointingConfig.maxRuntime as StringValue) // type assertion to satisfy ms package
          : (this.options.checkpointingConfig.maxRuntime as number);

      // 0 or negative max runtime? Skip.
      if (Number.isFinite(maxRuntimeMs) && maxRuntimeMs > 0) {
        this.checkpointingMaxRuntimeTimer = createTimeoutPromise(maxRuntimeMs);

        void this.checkpointingMaxRuntimeTimer.then(async () => {
          await this.middlewareManager.onMemoizationEnd();
          state.setCheckpoint({
            type: "checkpointing-runtime-reached",
          });
        });
      }
    }

    if (this.options.checkpointingConfig?.maxInterval) {
      const maxIntervalMs = Temporal.isTemporalDuration(
        this.options.checkpointingConfig.maxInterval,
      )
        ? this.options.checkpointingConfig.maxInterval.total({
            unit: "milliseconds",
          })
        : typeof this.options.checkpointingConfig.maxInterval === "string"
          ? ms(this.options.checkpointingConfig.maxInterval as StringValue) // type assertion to satisfy ms package
          : (this.options.checkpointingConfig.maxInterval as number);

      // 0 or negative max interval? Skip.
      if (Number.isFinite(maxIntervalMs) && maxIntervalMs > 0) {
        this.checkpointingMaxBufferIntervalTimer =
          createTimeoutPromise(maxIntervalMs);

        void this.checkpointingMaxBufferIntervalTimer.then(async () => {
          // Note that this will not immediately run; it will be queued like all
          // other checkpoints so that we're never running multiple checkpoints
          // at the same time and it's easier to reason about those decision
          // points.
          //
          // A change in the future may be to make this particular checkpointing
          // action immediate and have the checkpoint action itself be
          // idempotent.
          state.setCheckpoint({
            type: "checkpointing-buffer-interval-reached",
          });

          this.checkpointingMaxBufferIntervalTimer?.reset();
        });
      }
    }
  }
}

/**
 * Types of checkpoints that can be reached during execution.
 */
export interface Checkpoints {
  "steps-found": { steps: [FoundStep, ...FoundStep[]] };
  "function-rejected": { error: unknown };
  "function-resolved": { data: unknown };
  "step-not-found": {
    step: OutgoingOp;
    foundSteps: BasicFoundStep[];
    totalFoundSteps: number;
  };
  "checkpointing-runtime-reached": {};
  "checkpointing-buffer-interval-reached": {};
}

type Checkpoint = {
  [K in keyof Checkpoints]: Simplify<{ type: K } & Checkpoints[K]>;
}[keyof Checkpoints];

type CheckpointHandlers = Record<
  StepMode,
  {
    [C in Checkpoint as C["type"]]: (
      checkpoint: C,

      /**
       * This is the number of checkpoints that have been seen before this one was
       * triggered.
       *
       * The catch-all `""` checkpoint does not increment this count.
       */
      i: number,
    ) => MaybePromise<ExecutionResult | undefined>;
  } & {
    "": (
      checkpoint: Checkpoint,

      /**
       * This is the number of checkpoints that have been seen before this one was
       * triggered.
       *
       * The catch-all `""` checkpoint does not increment this count.
       */
      i: number,
    ) => MaybePromise<void>;
  }
>;

export interface ExecutionState {
  /**
   * A value that indicates that we're executing this step. Can be used to
   * ensure steps are not accidentally nested until we support this across all
   * platforms.
   */
  executingStep?: Readonly<Omit<OutgoingOp, "id">>;

  /**
   * A map of step IDs to their data, used to fill previously-completed steps
   * with state from the executor.
   */
  stepState: Record<string, MemoizedOp>;

  /**
   * Hashed defer step IDs the backend has already received. Used to skip
   * re-emitting `DeferAdd` ops on replay. Defer ops aren't memoized like
   * normal steps (they have no result), so this lives separately from
   * `stepState`.
   */
  priorDefers: Record<string, unknown>;

  /**
   * The number of steps we expect to fulfil based on the state passed from the
   * Executor.
   */
  stepsToFulfill: number;

  /**
   * A map of step IDs to their functions to run. The executor can request a
   * specific step to run, so we need to store the function to run here.
   */
  steps: Map<string, FoundStep>;

  /**
   * A flag which represents whether or not steps are understood to be used in
   * this function. This is used to determine whether or not we should run
   * some steps (such as `step.sendEvent`) inline as they are found.
   */
  hasSteps: boolean;

  /**
   * The core loop - a generator used to take an action upon finding the next
   * checkpoint. Manages the flow of execution and cleaning up after itself.
   */
  loop: AsyncGenerator<Checkpoint, void, void>;

  /**
   * A function that resolves the `Promise` returned by `waitForNextDecision`.
   */
  setCheckpoint: (data: Checkpoint) => void;

  /**
   * Returns whether or not all state passed from the executor has been used to
   * fulfill found steps.
   */
  allStateUsed: () => boolean;

  /**
   * An ordered list of step IDs that represents the order in which their
   * execution was completed.
   */
  stepCompletionOrder: string[];

  /**
   * An set of step IDs that have yet to be seen in this execution. Used to
   * decide when to trigger middleware based on the current state.
   */
  remainingStepsToBeSeen: Set<string>;

  /**
   * If defined, this is the error that purposefully thrown when memoizing step
   * state in order to support per-step errors.
   *
   * We use this so that if the function itself rejects with the same error, we
   * know that it was entirely uncaught (or at the very least rethrown), so we
   * should send a `NonRetriableError` to stop needless execution of a function
   * that will continue to fail.
   *
   * TODO This is imperfect, as this state is currently kept around for longer
   * than it needs to be. It should disappear as soon as we've seen that the
   * error did not immediately throw. It may need to be refactored to work a
   * little more smoothly with the core loop.
   */
  recentlyRejectedStepError?: StepError;

  /**
   * If defined, this indicates that we're running a checkpointed function run,
   * and contains the data needed to report progress back to Inngest.
   */
  checkpointedRun?: {
    fnId: string;
    appId: string;
    token?: string;
    realtimeToken: string;
  };

  /**
   * Set when the checkpointing max runtime has been reached. Rather than
   * immediately returning a DiscoveryRequest (which re-invokes user code
   * between steps), we defer until the next step boundary so the user's code
   * between steps can complete.
   */
  checkpointingRuntimeExceeded?: boolean;

  /**
   * A buffer of steps that are currently queued to be checkpointed.
   */
  checkpointingStepBuffer: OutgoingOp[];

  /**
   * Buffer for opcode-only sync ops (e.g. `DeferAdd`) awaiting shipment
   * on the next outbound wire message. See `ARCHITECTURE.md` and
   * `lazyOps.ts` for the rationale.
   */
  lazyOps: LazyOps;

  /**
   * Metadata collected during execution to be sent with outgoing ops.
   */
  metadata?: Map<string, Array<MetadataUpdate>>;
}

const hashId = (id: string): string => {
  return sha1().update(id).digest("hex");
};

const hashOp = (op: OutgoingOp): OutgoingOp => {
  return {
    ...op,
    id: hashId(op.id),
  };
};

/**
 * Result of resolving a step ID collision.
 */
interface CollisionResolutionResult {
  /** The final ID to use (either original or with index suffix). */
  finalId: string;

  /** The index used, if collision was detected. */
  index?: number;
}

/** Result of applying middleware to a step. */
interface MiddlewareApplicationResult {
  hashedId: string;
  isFulfilled: boolean;
  setActualHandler: (handler: () => Promise<unknown>) => void;
  stepInfo: Middleware.StepInfo;
  stepState: MemoizedOp | undefined;
  wrappedHandler: () => Promise<unknown>;
}

/**
 * Resolves step ID collisions by appending an index suffix if needed.
 * Consolidates the duplicated collision detection logic.
 *
 * @param baseId - The original step ID
 * @param stepsMap - Map of existing steps (keyed by hashed ID)
 * @param expectedIndexes - Map tracking expected next index for each base ID
 * @returns The final ID to use and optional index
 */
function resolveStepIdCollision({
  baseId,
  expectedIndexes,
  stepsMap,
}: {
  baseId: string;
  expectedIndexes: Map<string, number>;
  stepsMap: Map<string, FoundStep>;
}): CollisionResolutionResult {
  const hashedBaseId = hashId(baseId);

  // Check both stepsMap (steps added to state) and expectedIndexes (claimed by
  // concurrent in-progress step handlers that haven't been added to state yet).
  if (!stepsMap.has(hashedBaseId) && !expectedIndexes.has(baseId)) {
    // No collision. Claim this base ID so concurrent callers detect collision.
    expectedIndexes.set(baseId, 1);
    return { finalId: baseId };
  }

  // Collision detected. Find next available index
  const expectedNextIndex = expectedIndexes.get(baseId) ?? 1;
  const maxIndex = expectedNextIndex + stepsMap.size + 1;
  for (let i = expectedNextIndex; i < maxIndex; i++) {
    const indexedId = baseId + STEP_INDEXING_SUFFIX + i;
    const hashedIndexedId = hashId(indexedId);

    if (!stepsMap.has(hashedIndexedId)) {
      expectedIndexes.set(baseId, i + 1);
      return { finalId: indexedId, index: i };
    }
  }

  throw new UnreachableError(
    `Could not resolve step ID collision for "${baseId}" after ${stepsMap.size + 1} attempts`,
  );
}

function isNonEmpty<T>(arr: T[]): arr is [T, ...T[]] {
  return arr.length > 0;
}

/**
 * Exported for testing.
 */
export const _internals = { hashOp, hashId, resolveStepIdCollision };
